import React from 'react';

// ──────────────────────────────────────────────
// INDI Terms of Service
// Effective Date: March 2026
// ──────────────────────────────────────────────

export default function TermsOfService({ onBack }) {
  const sectionStyle = {
    marginBottom: '2rem',
  };
  const headingStyle = {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '1.25rem',
    fontWeight: '700',
    color: '#E8ECF1',
    marginBottom: '0.75rem',
  };
  const textStyle = {
    fontFamily: "'Instrument Sans', sans-serif",
    fontSize: '0.9375rem',
    lineHeight: '1.7',
    color: '#94A3B8',
    marginBottom: '0.75rem',
  };
  const listItemStyle = {
    ...textStyle,
    paddingLeft: '1.25rem',
    position: 'relative',
    marginBottom: '0.4rem',
  };
  const dotStyle = {
    position: 'absolute',
    left: 0,
    color: '#06B6D4',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#060A13',
      color: '#E8ECF1',
      fontFamily: "'Instrument Sans', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Instrument+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
      `}</style>

      <div style={{
        maxWidth: '740px',
        margin: '0 auto',
        padding: '4rem 2rem',
      }}>
        {/* Back link */}
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#06B6D4',
            fontFamily: "'Instrument Sans', sans-serif",
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            marginBottom: '2rem',
            padding: 0,
          }}
        >
          ← Back to INDI
        </button>

        {/* Header */}
        <h1 style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: '2.25rem',
          fontWeight: '800',
          letterSpacing: '-0.03em',
          marginBottom: '0.5rem',
        }}>
          Terms of Service
        </h1>
        <p style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '0.8125rem',
          color: '#64748B',
          marginBottom: '3rem',
        }}>
          Effective Date: March 4, 2026 &nbsp;|&nbsp; Last Updated: March 4, 2026
        </p>

        {/* Introduction */}
        <div style={sectionStyle}>
          <p style={textStyle}>
            Welcome to INDI. These Terms of Service ("Terms") govern your access to and use of the 
            website located at indi.space (the "Site"), operated by INDI Platform LLC ("INDI," "we," 
            "us," or "our"). By accessing or using the Site, you agree to be bound by these Terms. 
            If you do not agree, please do not use the Site.
          </p>
        </div>

        {/* 1. About the Site */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>1. About the Site</h2>
          <p style={textStyle}>
            The Site is currently an informational website that describes the INDI platform, 
            collects community signups, and accepts filmmaker applications and investor interest 
            registrations. The Site does not currently offer, sell, or facilitate the purchase of 
            any securities, tokens, or investment products.
          </p>
          <p style={{
            ...textStyle,
            padding: '1rem',
            background: 'rgba(6, 182, 212, 0.04)',
            border: '1px solid rgba(6, 182, 212, 0.1)',
            borderRadius: '10px',
            color: '#B0B8C8',
          }}>
            Nothing on this Site constitutes an offer to sell or a solicitation of an offer 
            to buy any securities. Any future securities offerings will be conducted in compliance 
            with applicable federal and state securities laws, including SEC Regulation Crowdfunding 
            (Regulation CF), and will be accompanied by appropriate offering documents.
          </p>
        </div>

        {/* 2. Eligibility */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>2. Eligibility</h2>
          <p style={textStyle}>
            You must be at least 18 years of age to use the Site. By using the Site, you 
            represent and warrant that you meet this requirement and have the legal capacity 
            to enter into these Terms.
          </p>
        </div>

        {/* 3. User Submissions */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>3. User Submissions</h2>
          <p style={textStyle}>
            When you submit information through the Site (including community signups, filmmaker 
            applications, and investor interest forms), you represent that:
          </p>
          <div style={listItemStyle}><span style={dotStyle}>·</span>The information you provide is accurate and complete</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>You have the right to submit such information</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>Your submission does not violate any third-party rights or applicable laws</div>
          <p style={{ ...textStyle, marginTop: '0.75rem' }}>
            Submitting a filmmaker application or investor interest registration does not 
            guarantee acceptance into any future program, offering, or platform feature. 
            All applications and registrations are subject to review at INDI's sole discretion.
          </p>
        </div>

        {/* 4. Intellectual Property */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>4. Intellectual Property</h2>
          <p style={textStyle}>
            All content on the Site — including text, graphics, logos, design elements, and 
            software — is the property of INDI Platform LLC or its licensors and is protected 
            by applicable intellectual property laws. You may not reproduce, distribute, modify, 
            or create derivative works from any content on the Site without our prior written consent.
          </p>
          <p style={textStyle}>
            The INDI name, logo, and all related marks are trademarks of INDI Holdings LLC. 
            Unauthorized use of these marks is prohibited.
          </p>
        </div>

        {/* 5. Prohibited Conduct */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>5. Prohibited Conduct</h2>
          <p style={textStyle}>You agree not to:</p>
          <div style={listItemStyle}><span style={dotStyle}>·</span>Use the Site for any unlawful purpose</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>Submit false, misleading, or fraudulent information</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>Attempt to gain unauthorized access to any part of the Site or its systems</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>Interfere with or disrupt the operation of the Site</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>Scrape, harvest, or collect information from the Site through automated means</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>Impersonate any person or entity</div>
        </div>

        {/* 6. Disclaimer of Warranties */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>6. Disclaimer of Warranties</h2>
          <p style={textStyle}>
            THE SITE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
            EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF 
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO 
            NOT WARRANT THAT THE SITE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
          </p>
        </div>

        {/* 7. Limitation of Liability */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>7. Limitation of Liability</h2>
          <p style={textStyle}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, INDI PLATFORM LLC AND ITS AFFILIATES, 
            OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, 
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED 
            TO YOUR USE OF THE SITE, WHETHER BASED ON WARRANTY, CONTRACT, TORT, OR ANY OTHER 
            LEGAL THEORY, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
        </div>

        {/* 8. Indemnification */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>8. Indemnification</h2>
          <p style={textStyle}>
            You agree to indemnify, defend, and hold harmless INDI Platform LLC and its 
            affiliates from and against any claims, liabilities, damages, losses, and 
            expenses (including reasonable attorneys' fees) arising out of or related to 
            your use of the Site, your violation of these Terms, or your violation of any 
            rights of a third party.
          </p>
        </div>

        {/* 9. Forward-Looking Statements */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>9. Forward-Looking Statements</h2>
          <p style={textStyle}>
            The Site may contain forward-looking statements regarding INDI's plans, objectives, 
            expectations, and intentions. These statements are based on current expectations 
            and are subject to risks and uncertainties that could cause actual results to differ 
            materially. Forward-looking statements include, but are not limited to, descriptions 
            of planned platform features, roadmap timelines, and business strategies. We undertake 
            no obligation to update any forward-looking statements.
          </p>
        </div>

        {/* 10. Investment Disclaimer */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>10. Investment Disclaimer</h2>
          <p style={{
            ...textStyle,
            padding: '1rem',
            background: 'rgba(239, 68, 68, 0.04)',
            border: '1px solid rgba(239, 68, 68, 0.1)',
            borderRadius: '10px',
            color: '#B0B8C8',
          }}>
            Nothing on this Site should be construed as investment advice, a recommendation 
            to buy or sell any security, or an offer or solicitation of an offer to buy or 
            sell any security. Investing in securities, including securities offered under 
            SEC Regulation Crowdfunding, involves significant risks including the possible 
            loss of your entire investment. Past performance is not indicative of future results. 
            You should consult with your own financial, legal, and tax advisors before making 
            any investment decisions.
          </p>
        </div>

        {/* 11. Governing Law */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>11. Governing Law</h2>
          <p style={textStyle}>
            These Terms shall be governed by and construed in accordance with the laws of the 
            State of Delaware, without regard to its conflict of law provisions. Any disputes 
            arising under these Terms shall be resolved exclusively in the state or federal 
            courts located in the State of Delaware.
          </p>
        </div>

        {/* 12. Changes to Terms */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>12. Changes to These Terms</h2>
          <p style={textStyle}>
            We reserve the right to modify these Terms at any time. When we do, we will revise 
            the "Last Updated" date at the top of this page. Your continued use of the Site 
            after any modifications constitutes your acceptance of the revised Terms. We 
            encourage you to review these Terms periodically.
          </p>
        </div>

        {/* 13. Severability */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>13. Severability</h2>
          <p style={textStyle}>
            If any provision of these Terms is found to be unenforceable or invalid, that 
            provision shall be limited or eliminated to the minimum extent necessary, and the 
            remaining provisions shall remain in full force and effect.
          </p>
        </div>

        {/* 14. Contact */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>14. Contact Us</h2>
          <p style={textStyle}>
            If you have questions about these Terms, please contact us at:
          </p>
          <div style={{
            padding: '1.25rem',
            background: 'rgba(6, 182, 212, 0.04)',
            border: '1px solid rgba(6, 182, 212, 0.1)',
            borderRadius: '12px',
            marginTop: '0.75rem',
          }}>
            <p style={{ ...textStyle, marginBottom: '0.25rem', color: '#E8ECF1', fontWeight: '600' }}>INDI Platform LLC</p>
            <p style={{ ...textStyle, marginBottom: '0' }}>Email: info@indi.space</p>
          </div>
        </div>
      </div>
    </div>
  );
}
